import { AxiosResponse } from 'axios';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query';
import scheduleService, {
  IFetchScheduleParams,
  ITimeBoxRequestData,
} from '../../services/scheduleService';
import {
  IScheduleDTO,
  ITimeBoxDTO,
} from '../../shared/interfaces/schedule-interfaces';

enum ScheduleKeys {
  listSchedules = 'listSchedules',
  listTimeBoxByService = 'listTimeBoxByService',
}

const fetchSchedules = async (params?: IFetchScheduleParams) => {
  const response = await scheduleService.list(params);

  return response.data;
};

const fetchTimeBox = async (requestData: ITimeBoxRequestData) => {
  const response = await scheduleService.listAvailableSchedulesByService(
    requestData,
  );

  return response.data;
};

export const useGetSchedules = (
  params?: IFetchScheduleParams,
): UseQueryResult<IScheduleDTO[]> => {
  return useQuery(
    [ScheduleKeys.listSchedules, params],
    () => fetchSchedules(params),
    {
      staleTime: 1000 * 60 * 3,
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );
};

export const useCreateSchedule: () => UseMutationResult<
  AxiosResponse<IScheduleDTO>,
  any,
  Omit<IScheduleDTO, 'id'>
> = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: Omit<IScheduleDTO, 'id'>) => scheduleService.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(ScheduleKeys.listSchedules);
      },
    },
  );
};

interface IMutationUpdateProps {
  scheduleId: string;
}

export const useUpdateSchedule: (
  props: IMutationUpdateProps,
) => UseMutationResult<
  AxiosResponse<IScheduleDTO>,
  any,
  Omit<IScheduleDTO, 'id'>
> = ({ scheduleId }) => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: Omit<IScheduleDTO, 'id'>) =>
      scheduleService.update(scheduleId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(ScheduleKeys.listSchedules);
      },
    },
  );
};

interface IMutationDeleteProps {
  id: string;
}

export const useDeleteSchedule: (
  props: IMutationDeleteProps,
) => UseMutationResult<AxiosResponse, Error> = ({ id }) => {
  const queryClient = useQueryClient();

  return useMutation(() => scheduleService.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(ScheduleKeys.listSchedules);
    },
  });
};

export const useGetAppointments = (
  requestData: ITimeBoxRequestData,
): UseQueryResult<ITimeBoxDTO[]> => {
  return useQuery(
    [ScheduleKeys.listTimeBoxByService, requestData],
    () => fetchTimeBox(requestData),
    {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      retry: 2,
    },
  );
};
